import {Component, OnInit, OnDestroy, inject} from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { LaunchDarklyService } from './services/launch-darkly.service';
import {datadogRum} from '@datadog/browser-rum';
import {PosthogService} from './services/posthog.service';
import {SessionPingService} from './services/session-ping.service';
import { resolveHome } from './shared/utils/auth-groups.util';
import { EnvBannerComponent } from './shared/ui/env-banner/env-banner.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, EnvBannerComponent],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'frontend';
  private hubListenerCancel: (() => void) | undefined;
  posthogService = inject(PosthogService);
  sessionPing = inject(SessionPingService);

  constructor(private launchdarkly: LaunchDarklyService, private router: Router) {}

  ngOnInit(): void {
    // Initialize PostHog without user identification
    this.posthogService.initPostHog();

    // Listen for all Auth events
    this.hubListenerCancel = Hub.listen('auth', async ({ payload }) => {
      const { event } = payload;
      if (event === 'signedIn' || event === 'signInWithRedirect') {
        await this.updateUserContext();
        await this.handlePostLoginRedirect();
      } else if (event === 'signedOut') {
        this.launchdarkly.closeClient();
        this.posthogService.reset(); // Reset PostHog on sign out
        datadogRum.clearUser();
        this.sessionPing.stop();
      }
    });

    // Also populate context on initial load (in case user is already signed in)
    this.updateUserContext();

    // Handle redirect on initial load if user is already authenticated
    this.handlePostLoginRedirect();
  }


  private async updateUserContext(): Promise<void> {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.payload;
      if (!idToken) {
        console.error('No ID token payload found');
        return;
      }

      const userId = idToken.sub!;
      const email = typeof idToken['email'] === 'string'
        ? idToken['email'].toLowerCase()
        : '';
      const roles = (idToken['cognito:groups'] as string[]) || [];

      // Identify user in PostHog and start session replay
      this.posthogService.identifyUser(userId, email, {
        roles: roles,
        name: email,
      });
      datadogRum.setUser({ id: userId, email, name: email, roles });

      // Pulsation de présence, mesurée par l'application elle-même et non par
      // PostHog : un bloqueur de publicité ne peut pas la faire disparaître,
      // et un écran qui répond « est-ce qu'ils utilisent l'app » vaut par ses
      // zéros. Voir session-ping.service.ts et la migration 0096.
      // Démarrée ICI, après que l'identité est résolue : `updateUserContext`
      // est appelée à la connexion ET au chargement d'une session déjà
      // ouverte, et `start()` est idempotent.
      this.sessionPing.start();
    } catch (err) {
      console.error('Error fetching auth session:', err);
    }
  }

  private async handlePostLoginRedirect(): Promise<void> {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.payload;

      if (!idToken) {
        console.error('No ID token found after sign-in');
        return;
      }

      const userGroups: string[] = (idToken['cognito:groups'] as string[]) || [];

      if (this.router.url === '/login') {
        // Même décision qu'à la connexion et dans les gardes : un seul endroit
        // sait quelle page ouvre quel jeu de groupes.
        const home = resolveHome(userGroups);
        console.log(`Post-login redirect for groups [${userGroups.join(',')}] -> ${home}`);
        this.router.navigate([home]);
      }
    } catch (err) {
      console.error('Error handling post-login redirect:', err);
    }
  }

  ngOnDestroy(): void {
    // Stop listening to Hub events
    this.hubListenerCancel?.();
    this.launchdarkly.closeClient();
    this.posthogService.reset();
    this.sessionPing.stop();
  }
}
